/**
 * MultiSelect
 * Reusable multi-select dropdown with search, checkboxes, select all, and clear all.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

export default function MultiSelect({
  label,
  options,
  selected,
  onChange,
  placeholder,
  maxSelections,
  required,
  error,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggle = useCallback((value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      if (maxSelections && selected.length >= maxSelections) return;
      onChange([...selected, value]);
    }
  }, [selected, onChange, maxSelections]);

  const handleSelectAll = useCallback(() => {
    const allValues = filteredOptions.map((o) => o.value);
    if (maxSelections) {
      onChange(allValues.slice(0, maxSelections));
    } else {
      onChange(allValues);
    }
  }, [filteredOptions, onChange, maxSelections]);

  const handleClearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {label && (
        <label style={labelStyle}>
          {label}
          {required && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
        </label>
      )}

      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          ...inputStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          borderColor: error ? '#ef4444' : isOpen ? '#3b82f6' : '#d1d5db',
          backgroundColor: '#fff',
          textAlign: 'left',
        }}
      >
        <span style={{ color: selected.length > 0 ? '#1e293b' : '#94a3b8', fontSize: '14px' }}>
          {selected.length > 0
            ? `${selected.length} selected`
            : placeholder || 'Select options...'}
        </span>
        <span style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          {selected.length > 0 && (
            <span style={{
              backgroundColor: '#3b82f6',
              color: '#fff',
              fontSize: '11px',
              fontWeight: '700',
              padding: '2px 7px',
              borderRadius: '10px',
              lineHeight: '1.4',
            }}>
              {selected.length}
            </span>
          )}
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.15s ease',
          }}>
            <path d="M3 4.5L6 7.5L9 4.5" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {error && <div style={errorStyle}>{error}</div>}

      {/* Dropdown */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 50,
          marginTop: '4px',
          backgroundColor: '#fff',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          maxHeight: '280px',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Search input */}
          <div style={{ padding: '8px', borderBottom: '1px solid #f1f5f9' }}>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              style={{
                width: '100%',
                padding: '6px 10px',
                fontSize: '13px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              autoFocus
            />
          </div>

          {/* Select All / Clear All */}
          <div style={{
            display: 'flex',
            gap: '8px',
            padding: '6px 8px',
            borderBottom: '1px solid #f1f5f9',
          }}>
            <button
              type="button"
              onClick={handleSelectAll}
              style={actionBtnStyle}
            >
              Select All
            </button>
            <button
              type="button"
              onClick={handleClearAll}
              style={actionBtnStyle}
            >
              Clear All
            </button>
          </div>

          {/* Options list */}
          <div style={{ overflowY: 'auto', maxHeight: '200px', padding: '4px 0' }}>
            {filteredOptions.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', fontSize: '13px', color: '#94a3b8' }}>
                No options found
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = selected.includes(opt.value);
                const isDisabled = !isSelected && maxSelections && selected.length >= maxSelections;
                return (
                  <label
                    key={opt.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 12px',
                      fontSize: '13px',
                      color: isDisabled ? '#cbd5e1' : '#374151',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      backgroundColor: isSelected ? '#f0f9ff' : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggle(opt.value)}
                      disabled={isDisabled}
                      style={{ width: '16px', height: '16px', cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                    />
                    {opt.label}
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

MultiSelect.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  maxSelections: PropTypes.number,
  required: PropTypes.bool,
  error: PropTypes.string,
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '4px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '14px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  backgroundColor: '#fff',
  color: '#1e293b',
  outline: 'none',
  boxSizing: 'border-box',
};

const actionBtnStyle = {
  padding: '4px 10px',
  fontSize: '12px',
  fontWeight: '600',
  color: '#3b82f6',
  backgroundColor: '#f0f9ff',
  border: '1px solid #bfdbfe',
  borderRadius: '6px',
  cursor: 'pointer',
};

const errorStyle = {
  fontSize: '12px',
  color: '#ef4444',
  marginTop: '3px',
};
